/**
 * HOOKS REACT QUERY POUR LES TAGS
 * ===============================
 * 
 * Hooks personnalisés pour la gestion complète des tags avec cache,
 * optimistic updates, et synchronisation temps-réel.
 */

import { 
  useQuery, 
  useMutation, 
  useQueryClient
} from '@tanstack/react-query';
import { toast } from '@/hooks/use-toast';
import { tagsService } from '../services/tags';
import { entityTagsQueryKeys } from './useEntityTags'; // Import pour invalidation croisée
import type {
  TagCreateRequest,
  TagUpdateRequest,
  TagFilter,
  EntityTagsUpdate,
  BulkTagOperation,
  EntityType,
  Tag,
  TagSummary,
  TagWithChildren,
  TagStats,
} from '../types/tags';

// =============================================
// CLÉS DE CACHE REACT QUERY
// =============================================

export const tagKeys = {
  all: ['tags'] as const,
  lists: () => [...tagKeys.all, 'list'] as const,
  list: (filters?: TagFilter) => [...tagKeys.lists(), filters] as const,
  details: () => [...tagKeys.all, 'detail'] as const,
  detail: (id: number) => [...tagKeys.details(), id] as const,
  hierarchy: () => [...tagKeys.all, 'hierarchy'] as const,
  summary: () => [...tagKeys.all, 'summary'] as const,
  stats: (entityType?: EntityType) => [...tagKeys.all, 'stats', entityType] as const,
  entityTags: (entityType: EntityType, entityId: number) => 
    ['entityTags', entityType, entityId] as const,
  suggestions: (entityType?: EntityType, context?: string) => 
    [...tagKeys.all, 'suggestions', entityType, context] as const,
  search: (query: string, filters?: Partial<TagFilter>) => 
    [...tagKeys.all, 'search', query, filters] as const,
};

// =============================================
// HOOKS POUR LES REQUÊTES (READ)
// =============================================

/**
 * Hook pour récupérer la liste des tags avec filtres optionnels
 */
export function useTags(filters?: TagFilter) {
  return useQuery<Tag[]>({
    queryKey: tagKeys.list(filters),
    queryFn: () => tagsService.getTags(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook pour récupérer un tag par son ID
 */
export function useTag(tagId: number) {
  return useQuery<Tag>({
    queryKey: tagKeys.detail(tagId),
    queryFn: () => tagsService.getTag(tagId),
    enabled: !!tagId,
  });
}

/**
 * Hook pour récupérer la hiérarchie complète des tags
 */
export function useTagHierarchy() {
  return useQuery<TagWithChildren[]>({
    queryKey: tagKeys.hierarchy(),
    queryFn: () => tagsService.getTagHierarchy(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook pour récupérer le résumé des tags de l'utilisateur
 */
export function useTagSummary() {
  return useQuery<TagSummary>({
    queryKey: tagKeys.summary(),
    queryFn: () => tagsService.getTagSummary(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook pour récupérer les statistiques d'utilisation des tags
 */
export function useTagStats(entityType?: EntityType) {
  return useQuery<TagStats[]>({
    queryKey: tagKeys.stats(entityType),
    queryFn: () => tagsService.getTagStats(entityType),
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
}

/**
 * Hook pour récupérer les tags associés à une entité
 */
export function useEntityTags(entityType: EntityType, entityId: number) {
  return useQuery<Tag[]>({
    queryKey: tagKeys.entityTags(entityType, entityId),
    queryFn: () => tagsService.getEntityTags(entityType, entityId),
    enabled: !!entityId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook pour récupérer les suggestions de tags
 */
export function useTagSuggestions(entityType?: EntityType, context?: string) {
  return useQuery<any[]>({
    queryKey: tagKeys.suggestions(entityType, context),
    queryFn: () => tagsService.getSuggestedTags(entityType, context),
    staleTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook pour la recherche de tags avec auto-complétion
 */
export function useTagSearch(query: string, filters?: Partial<TagFilter>, enabled = true) {
  return useQuery<Tag[]>({
    queryKey: tagKeys.search(query, filters),
    queryFn: () => tagsService.searchTags(query, filters),
    enabled: enabled && query.length >= 2, // Chercher seulement avec au moins 2 caractères
    staleTime: 2 * 60 * 1000,
  });
}

// =============================================
// HOOKS POUR LES MUTATIONS (WRITE)
// =============================================

/**
 * Hook pour créer un nouveau tag
 */
export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagData: TagCreateRequest) => tagsService.createTag(tagData),
    onSuccess: (newTag) => {
      // Invalider les caches pertinents
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: tagKeys.summary() });
      
      // Ajouter le nouveau tag au cache
      queryClient.setQueryData(tagKeys.detail(newTag.id), newTag);

      console.log(`Tag créé: ${newTag.name}`);
    },
    onError: (error: any) => {
      console.error('Erreur lors de la création du tag:', error);
    },
  });
}

/**
 * Hook pour mettre à jour un tag
 */
export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, tagData }: { tagId: number; tagData: TagUpdateRequest }) =>
      tagsService.updateTag(tagId, tagData),
    onSuccess: (updatedTag, { tagId }) => {
      // Mettre à jour le cache du tag spécifique
      queryClient.setQueryData(tagKeys.detail(tagId), updatedTag);
      
      // Invalider les listes et hiérarchies
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: tagKeys.summary() });

      console.log(`Tag mis à jour: ${updatedTag.name}`);
    },
    onError: (error: any) => {
      console.error('Erreur lors de la mise à jour du tag:', error);
    },
  });
}

/**
 * Hook pour supprimer un tag
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: number) => tagsService.deleteTag(tagId),
    onSuccess: (_, tagId) => {
      // Supprimer du cache
      queryClient.removeQueries({ queryKey: tagKeys.detail(tagId) });
      
      // Invalider les listes et hiérarchies
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });
      queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: tagKeys.summary() });

      console.log('Tag supprimé avec succès');
    },
    onError: (error: any) => {
      console.error('Erreur lors de la suppression du tag:', error);
    },
  });
}

/**
 * Hook pour déplacer un tag dans la hiérarchie
 */
export function useMoveTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, newParentId }: { tagId: number; newParentId: number | null }) =>
      tagsService.moveTag(tagId, newParentId),
    onSuccess: (movedTag, { tagId }) => {
      // Mettre à jour le cache du tag déplacé
      queryClient.setQueryData(tagKeys.detail(tagId), movedTag);
      
      // Invalider la hiérarchie complètement
      queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() });

      toast({
        title: 'Tag déplacé',
        description: `Le tag "${movedTag.name}" a été déplacé avec succès.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur lors du déplacement',
        description: error.response?.data?.detail || 'Impossible de déplacer le tag.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook pour mettre à jour les tags d'une entité
 */
export function useUpdateEntityTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      entityType, 
      entityId, 
      tagsUpdate 
    }: { 
      entityType: EntityType; 
      entityId: number; 
      tagsUpdate: EntityTagsUpdate 
    }) =>
      tagsService.updateEntityTags(entityType, entityId, tagsUpdate),
    onSuccess: (updatedTags, { entityType, entityId }) => {
      // Mettre à jour le cache des tags de l'entité (useTags)
      queryClient.setQueryData(tagKeys.entityTags(entityType, entityId), updatedTags);
      
      // CORRECTIF : Invalider tous les caches useTags (forme objet attendue par les tests)
      queryClient.invalidateQueries({ queryKey: tagKeys.lists(), refetchType: 'active' });
      queryClient.invalidateQueries({ queryKey: ['tags'], refetchType: 'active' });
      
      // Invalider la hiérarchie pour mettre à jour les compteurs
      queryClient.invalidateQueries({ queryKey: tagKeys.hierarchy() });
      
      // Invalider les statistiques et le résumé
      queryClient.invalidateQueries({ queryKey: tagKeys.stats() });
      queryClient.invalidateQueries({ queryKey: tagKeys.summary() });
      
      // *** INVALIDATION CROISÉE : Invalider les caches du module useEntityTags ***
      queryClient.invalidateQueries({ queryKey: entityTagsQueryKeys.entity(entityType as string, entityId) });
      queryClient.invalidateQueries({ queryKey: entityTagsQueryKeys.popularTags(entityType as string) });
      queryClient.invalidateQueries({ queryKey: entityTagsQueryKeys.tagStats(entityType as string) });
      
      // Force un refetch immédiat pour les caches croisés
      queryClient.refetchQueries({ queryKey: entityTagsQueryKeys.entity(entityType as string, entityId) });

      toast({
        title: 'Tags mis à jour',
        description: 'Les tags ont été mis à jour avec succès.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur lors de la mise à jour',
        description: error.response?.data?.detail || 'Impossible de mettre à jour les tags.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook pour les opérations en lot sur les tags
 */
export function useBulkTagOperation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ entityType, operation }: { entityType: EntityType; operation: BulkTagOperation }) =>
      tagsService.bulkTagOperation(entityType, operation),
    onSuccess: (result, { entityType }) => {
      // Invalider tous les caches d'entité de ce type
      queryClient.invalidateQueries({ 
        predicate: (query) => 
          query.queryKey[0] === 'entityTags' && query.queryKey[1] === entityType
      });
      
      // Invalider les statistiques
      queryClient.invalidateQueries({ queryKey: tagKeys.stats() });
      queryClient.invalidateQueries({ queryKey: tagKeys.summary() });

      toast({
        title: 'Opération terminée',
        description: `${result.success_count} éléments traités avec succès.`,
      });

      if (result.error_count > 0) {
        toast({
          title: 'Erreurs détectées',
          description: `${result.error_count} erreurs lors de l'opération.`,
          variant: 'destructive',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur lors de l\'opération',
        description: error.response?.data?.detail || 'Opération échouée.',
        variant: 'destructive',
      });
    },
  });
}

// =============================================
// HOOKS UTILITAIRES
// =============================================

/**
 * Hook pour fusionner deux tags
 */
export function useMergeTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sourceTagId, targetTagId }: { sourceTagId: number; targetTagId: number }) =>
      tagsService.mergeTags(sourceTagId, targetTagId),
    onSuccess: (mergedTarget, { sourceTagId, targetTagId }) => {
      // Nettoyer le cache du tag source (fusionné)
      queryClient.removeQueries({ queryKey: tagKeys.detail(sourceTagId) });

      // Mettre à jour le cache du tag cible avec la réponse de fusion
      queryClient.setQueryData(tagKeys.detail(targetTagId), mergedTarget);

      // Invalider l'ensemble des caches tags
      queryClient.invalidateQueries({ queryKey: tagKeys.all });

      toast({
        title: 'Tags fusionnés',
        description: 'Les tags ont été fusionnés avec succès.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur lors de la fusion',
        description: error.response?.data?.detail || 'Impossible de fusionner les tags.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook pour nettoyer les tags inutilisés
 */
export function useCleanupTags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => tagsService.cleanupUnusedTags(),
    onSuccess: (result) => {
      // Invalider tous les caches
      queryClient.invalidateQueries({ queryKey: tagKeys.all });

      toast({
        title: 'Nettoyage terminé',
        description: `${result.deleted_count} tags inutilisés supprimés.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Erreur lors du nettoyage',
        description: error.response?.data?.detail || 'Impossible de nettoyer les tags.',
        variant: 'destructive',
      });
    },
  });
}
